import React, { useState, useCallback, useEffect, useMemo } from 'react';

import { Column } from 'primereact/column';
import { useSelector } from 'react-redux';
import { addMonths } from 'date-fns';

import { Container } from './styles';

import FinBoletoService from '../../services/FinBoletoService';
import AuthService from '../../services/AuthService';
import history from '../../services/history';
import {
  getPageParams,
  errorHandle,
  calcNaxItemsPage,
  isScreenMobile,
  formatDate,
  formatFloat,
  sanitizeFileName,
} from '../../util/functions';

import { StateScreen } from '../constants';
import CrudHeader from '../../components/CrudHeader';
import DataTableSp from '../../components/DataTableSp';
import LabelSp from '../../components/LabelSp';
import InputMaskSp from '../../components/InputMaskSp';
import RevendaService from '../../services/RevendaService';
import DropdownSp from '../../components/DropdownSp';
import BoletoCrud from './crud';
import ButtonSp from '../../components/ButtonSp';

export default function FinBoleto(props) {
  // useSelectors
  const filterVisible = useSelector(state => state.global.filterVisible);

  // useMemo
  const pageParams = useMemo(() => getPageParams(props.match.params, props.location.search), [
    props.location.search,
    props.match.params,
  ]);

  const filterService = useMemo(() => {
    const f = FinBoletoService.getFilter();
    f.idRevenda = AuthService.getUsuario().idRevenda;
    return f;
  }, []);

  // useStates
  const toBack = pageParams.toBack || '/boletos';

  const [filter, setFilter] = useState(filterService);
  const [boletos, setBoletos] = useState([]);

  const [pageLimit, setPageLimit] = useState(filterService.limit);
  const [first, setFirst] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  const [pageSelected, setPageSelected] = useState(0);
  const [revendas, setRevendas] = useState();
  const [funcTimeOut, setFuncTimeOut] = useState();
  const [tipoPessoaSelecionada, setTipoPessoaSelecionada] = useState('J');

  // useCallbacks

  const calcLimit = useCallback(() => {
    let ret;
    if (filterVisible) {
      ret = calcNaxItemsPage(8, 12, 17);
    } else {
      ret = calcNaxItemsPage(13, 17, 22);
    }
    setPageLimit(ret);
    return ret;
  }, [filterVisible]);

  const loadRevenda = useCallback(async () => {
    const r = await RevendaService.findAll({
      limit: 999,
    });

    const retorno = r.items.map(e => {
      return {
        label: e.nome,
        value: e.id,
      };
    });

    setRevendas(retorno);
  }, []);

  const handleBuscar = useCallback(
    async (_filter, _page, resetPage = true) => {
      _filter.page = _page || 0;
      _filter.limit = calcLimit();

      try {
        const result = await FinBoletoService.findAll({ ..._filter, resumo: 'resumo' });
        setBoletos(result.items);

        setTotalRecords(result.totalRecords);
        if (resetPage) {
          setFirst(0);
        }
      } catch (err) {
        errorHandle(err);
      }
    },
    [calcLimit]
  );

  const setFilterAndSearch = useCallback(
    async _filterValue => {
      if (JSON.stringify(_filterValue) !== JSON.stringify(filter)) {
        setFilter(_filterValue);

        if (funcTimeOut) {
          clearTimeout(funcTimeOut);
        }
        const func = setTimeout(async () => {
          handleBuscar(_filterValue);
        }, 800);
        setFuncTimeOut(func);
      }
    },
    [filter, funcTimeOut, handleBuscar]
  );

  function onPage(event) {
    const pagina = event.first / event.rows;
    setPageSelected(pagina);
    setFirst(event.first);
    handleBuscar(filter, pagina, false);
  }

  // useCallbacks

  // functions
  function getTitle() {
    const titleDefault = 'Boleto';
    let titleAdd = '';

    if (pageParams.stateScreen === StateScreen.stSearch) {
      titleAdd = '';
    }
    if (pageParams.stateScreen === StateScreen.stInsert) {
      titleAdd = '(Novo)';
    }
    if (pageParams.stateScreen === StateScreen.stUpdate) {
      titleAdd = `(Alterando Id: ${pageParams.idSelected})`;
    }
    if (pageParams.stateScreen === StateScreen.stView) {
      titleAdd = ` (Visualizando Id: ${pageParams.idSelected})`;
    }

    if (!isScreenMobile()) {
      return `${titleDefault} ${titleAdd}`;
    }

    return titleDefault;
  }

  function bloqueiaRevenda() {
    return (
      AuthService.getUsuario() &&
      AuthService.getUsuario().idRevenda &&
      !AuthService.isPerfilGerente()
    );
  }

  // useEffects
  useEffect(() => {
    if (pageParams.stateScreen === StateScreen.stSearch) {
      handleBuscar(filter, 0, true);
    }
    loadRevenda();
    // desativado para evitar que a cada vez que o cliente digitasse o sistema buscasse
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // useEffects
  // Responsavel por recalcular a quantidade de registros da grid quando a div do filtro ocultar
  // ou ficar visivel
  useEffect(() => {
    if (pageParams.stateScreen === StateScreen.stSearch) {
      calcLimit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calcLimit]);

  // renders
  return (
    <Container className="container-page page-enter-tab">
      <div className="p-grid">
        <CrudHeader
          title={getTitle()}
          disabledButtonInsert={!AuthService.checkRoles('CLIENTE_INSERIR')}
          showButtonFilter={pageParams.stateScreen === StateScreen.stSearch}
          showButtonInsert={false}
          showButtonSearch
          titleFilterDefault="Buscar por Nome"
          handleFilterDefault={text => {
            filter.nome = text;
            setFilter({ ...filter, nome: text });
            handleBuscar(filter);
          }}
          handleClearFilters={() => {
            setFilter({
              ...filter,
              nome: '',
              numeroContrato: '',
              idRevenda: null,
              sacadoCpfCnpj: '',
              tipoInativo: 0,
              dataInicio: addMonths(new Date(), -12 * 10),
              dataFim: new Date(),
            });
          }}
          handleButtonInsert={() => history.push('/boletos/insert')}
          handleButtonSearch={() => handleBuscar(filter)}
        >
          <div className="p-col-5 p-sm-2 p-lg-2 p-fluid">
            <LabelSp>Contrato</LabelSp>
            <InputMaskSp
              mask="999999"
              value={filter.numeroContrato}
              onChange={e => {
                setFilterAndSearch({ ...filter, numeroContrato: e?.value });
              }}
            />
          </div>

          <div className="p-col-5 p-sm-2 p-lg-2 p-fluid">
            <LabelSp>Tp. Pessoa</LabelSp>
            <DropdownSp
              value={tipoPessoaSelecionada}
              options={[
                { value: 'J', label: 'Jurídica' },
                { value: 'F', label: 'Física' },
              ]}
              onChange={e => {
                setTipoPessoaSelecionada(e?.value);
              }}
            />
          </div>
          {tipoPessoaSelecionada === 'F' ? (
            <div className="p-col-7 p-sm-3 p-lg-3 p-fluid">
              <LabelSp>CPF</LabelSp>
              <InputMaskSp
                mask="999.999.999-99"
                value={filter.sacadoCpfCnpj}
                onChange={e => {
                  setFilterAndSearch({ ...filter, sacadoCpfCnpj: e?.value });
                }}
              />
            </div>
          ) : (
            <div className="p-col-7 p-sm-3 p-lg-3 p-fluid">
              <LabelSp>CNPJ</LabelSp>
              <InputMaskSp
                mask="99.999.999/9999-99"
                value={filter.sacadoCpfCnpj}
                onChange={e => {
                  setFilterAndSearch({ ...filter, sacadoCpfCnpj: e?.value });
                }}
              />
            </div>
          )}
          <div className="p-col-12 p-md-5 p-lg-5 p-fluid">
            <LabelSp>Revenda</LabelSp>
            <DropdownSp
              placeholder="Todos"
              showClear
              disabled={bloqueiaRevenda()}
              onChange={e => {
                setFilterAndSearch({ ...filter, idRevenda: e?.value });
              }}
              options={revendas}
              value={filter.idRevenda}
            />
          </div>
        </CrudHeader>
        {pageParams.stateScreen === StateScreen.stSearch ? renderSearch() : renderCrud()}
      </div>
    </Container>
  );

  function renderSearch() {
    return (
      <>
        <div className="p-col-12 p-fluid datatable-doc-demo">
          <DataTableSp
            value={boletos}
            style={{ marginBottom: '2px' }}
            paginator
            rows={pageLimit}
            lazy
            responsive
            totalRecords={totalRecords}
            first={first}
            onPage={onPage}
          >
            <Column
              field="numeroContrato"
              header="Contrato"
              className="grid-col-id"
              style={{ width: 65 }}
            />
            <Column
              field="vencimento"
              className="grid-col-data"
              header="Vencto."
              body={rowData => formatDate(`${rowData.vencimento}`, 'dd/MM/yyyy')}
            />
            <Column
              field="sacadoCpfCnpj"
              className="grid-col p-p-5"
              style={{ width: 133 }}
              header="CPF/CNPJ"
            />
            <Column
              field="nomeFantasia"
              className="grid-col grid-col-nowrap"
              header="Fantasia"
              body={rowData => (
                <span title={rowData.sacadoFantasia}>{rowData.sacadoFantasia}</span>
              )}
            />
            <Column
              field="codigoBarras"
              className="grid-col grid-col-nowrap"
              header="Cód. Barras"
            />
            <Column
              field="valorDocumento"
              className="grid-col-val"
              header="Valor"
              body={rowData => formatFloat(`${rowData.valorDocumento}`)}
            />

            <Column
              className="gid-col-acoes-70"
              bodyStyle={{ textAlign: 'end' }}
              body={renderButtonOp}
            />
          </DataTableSp>
        </div>
      </>
    );
  }

  function renderButtonOp(rowData) {
    return (
      <>
        <ButtonSp
          className="botao-pequeno p-button-secondary buttons"
          title="Visualizar"
          icon="pi pi-search"
          onClick={() => history.push(`/boletos/${rowData.id}?view`)}
        />
        <ButtonSp
          className="botao-pequeno p-button-primary buttons"
          title="Baixar PDF"
          icon="pi pi-file-pdf"
          onClick={() => {
            getFile(`bol-${rowData.sacadoFantasia}.pdf`, rowData.nossoNumero);
          }}
        />
      </>
    );
  }

  function renderCrud() {
    return (
      <BoletoCrud
        idSelected={pageParams.idSelected}
        stateScreen={pageParams.stateScreen}
        onClose={() => {
          history.push(toBack);
          handleBuscar(filter, pageSelected, false);
        }}
      />
    );
  }
}

async function getFile(nomeArquivo, nossoNumero) {
  let file;

  const fileName = sanitizeFileName(nomeArquivo);

  try {
    file = await FinBoletoService.getBoletoPDF(nossoNumero);
  } catch (err) {
    errorHandle(err);
  }
  if (file) {
    const blob = new Blob([file]);
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    document.body.appendChild(link);
  }
}
